import React from "react";
import { Text } from "@react-three/drei";
import { InstallationType } from "../forms/Pergola_type";

const OFFSET_LABEL_LATO = 0.3;
const OFFSET_LABEL_INTERMEDIO = 0.4;

type OrientationLabelsProps = {
  larghezza: number;
  lunghezza: number;
  installazione: InstallationType;
  nstacchiX?: number;
  nstacchiY?: number;
};

const OrientationLabels: React.FC<OrientationLabelsProps> = ({
  larghezza,
  lunghezza,
  installazione,
  nstacchiX = 0,
  nstacchiY = 0,
}) => {
  // Calcola numero di segmenti
  const segmentiA = installazione === InstallationType.addossata_larghezza ? 1 : nstacchiX + 1;
  const segmentiC = installazione === InstallationType.addossata_larghezza ? 0 : nstacchiX + 1;
  const segmentiB = installazione === InstallationType.addossata_profondita ? 0 : nstacchiY + 1;
  const segmentiD = nstacchiY + 1;
  
  const stepX = larghezza / Math.max(1, segmentiA);
  const stepY = lunghezza / Math.max(1, segmentiB);
  const stepYD = lunghezza / Math.max(1, segmentiD);
  // Helper: etichette secondarie (A1, A2...)
  const renderSubLabels = (
    lato: "A" | "B" | "C" | "D",
    segmenti: number,
    getPosition: (i: number) => [number, number, number],
    rotation?: [number, number, number]
  ) => {
    if (segmenti <= 1) return null;

    return Array.from({ length: segmenti }, (_, i) => (
      <Text
        key={`${lato}${i + 1}`}
        position={getPosition(i)}
        fontSize={0.3}
        color="black"
        anchorX="center"
        anchorY="middle"
        rotation={rotation}
      >
        {lato}{i + 1}
      </Text>
    ));
  };

  const renderMainLabel = (
    label: string,
    position: [number, number, number],
    rotation?: [number, number, number]
  ) => (
    <Text
      position={position}
      fontSize={0.4}
      color="black"
      rotation={rotation}
    >
      {label}
    </Text>
  );

  return (
    <>
      {/* ==================== LATO A (Fronte) ==================== */}
      {installazione !== InstallationType.patio && (
        <>
          {/* Solo se 1 segmento → A, altrimenti A1, A2... */}
          {segmentiA === 1 ? (
            renderMainLabel("A", [larghezza / 2, -OFFSET_LABEL_LATO, 0])
          ) : (
            renderSubLabels(
              "A",
              segmentiA,
              (i) => [(i + 0.5) * stepX, -OFFSET_LABEL_INTERMEDIO, 0]
            )
          )}
        </>
      )}

      {/* ==================== LATO C (Retro) ==================== */}
      {installazione !== InstallationType.addossata_larghezza && installazione !== InstallationType.patio && installazione !== InstallationType.angolare && (
        <>
          {segmentiC === 1 ? (
            renderMainLabel("C", [larghezza / 2, lunghezza + OFFSET_LABEL_LATO, 0])
          ) : (
            renderSubLabels(
              "C",
              segmentiC,
              (i) => [(i + 0.5) * stepX, lunghezza + OFFSET_LABEL_INTERMEDIO, 0]
            )
          )}
        </>
      )}

      {/* ==================== LATO D (Destra) ==================== */}
      <>
        {segmentiD === 1 ? (
          renderMainLabel("D", [larghezza + OFFSET_LABEL_LATO, lunghezza / 2, 0], [0, 0, Math.PI / 2])
        ) : (
          renderSubLabels(
            "D",
            segmentiD,
            (i) => [larghezza + OFFSET_LABEL_INTERMEDIO, (i + 0.5) * stepYD, 0],
            [0, 0, Math.PI / 2]
          )
        )}
      </>

      {/* ==================== LATO B (Sinistra) ==================== */}
      {installazione !== InstallationType.addossata_profondita && installazione !== InstallationType.angolare && (
        <>
          {segmentiB === 1 ? (
            renderMainLabel("B", [-OFFSET_LABEL_LATO, lunghezza / 2, 0], [0, 0, -Math.PI / 2])
          ) : (
            renderSubLabels(
              "B",
              segmentiB,
              (i) => [-OFFSET_LABEL_INTERMEDIO, (i + 0.5) * stepY, 0],
              [0, 0, -Math.PI / 2]
            )
          )}
        </>
      )}
    </>
  );
};

export default OrientationLabels;